import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from './base.service';
import { SKIP_LOADING } from '../interceptors/error.interceptor';
import { Pagination } from '../../shared/models/page';
import {
  VisitCycleListItemDto,
  VisitCycleDetailsDto,
  VisitCycleDayDto,
  VisitCycleFilterOptionsDto,
  VisitCycleCalendarDto,
  VisitCycleRouteStopDto,
  VisitCycleRouteDto,
  VisitCycleExecutionSummaryDto,
  CreateVisitCycleRequest,
  UpdateVisitCycleRequest,
  AddDayRequest,
  AddRouteStopRequest,
  UpdateRouteStopRequest,
  VisitCycleFilters,
  VisitCycleCard,
  VisitCycleCardsResult,
  VisitCycleCardFilters,
  DayAssignmentChangeDto,
  CalendarChangesPreviewDto,
  CalendarChangesResultDto
} from '../../features/ciclo-visitacao/ciclo/models/ciclo';
import { ApplyWorkshopRequest, ReorderRouteEntryRequest, RouteOperationResult, RouteStopInput, WorkshopRouteConstraint } from '../../features/roteirizacao/models/route-operations';

/**
 * Diff atômico aplicado à lista de VisitPlans de um escopo do ciclo (template ou uma
 * única execução Planned). Cada campo é opcional/vazio quando não há mudança daquele
 * tipo — o backend vira um no-op quando os quatro estão vazios.
 *
 * - addedVisitPlanIds: VisitPlans adicionados ao escopo.
 * - removedVisitPlanIds: VisitPlans do escopo a serem removidos.
 * - newOrder: ordem final desejada dos VisitPlans após add+remove. Quando fornecido,
 *   dispara a regra de "limpar datas atribuídas/planejadas".
 * - consultantId: troca o consultor do escopo atomicamente com o resto.
 */
export interface VisitPlanScopeDiff {
  addedVisitPlanIds?: number[];
  removedVisitPlanIds?: number[];
  newOrder?: number[] | null;
  consultantId?: number | null;
}

@Injectable({
  providedIn: 'root'
})
export class VisitCycleService extends BaseService<VisitCycleListItemDto> {

  constructor(protected override http: HttpClient) {
    super(http, 'visit-cycle');
  }

  // ==================== Listagem e Filtros ====================

  getList(filters: VisitCycleFilters): Observable<Pagination<VisitCycleListItemDto>> {
    let params = new HttpParams();

    if (filters.searchTerm) {
      params = params.append('searchTerm', filters.searchTerm);
    }
    if (filters.consultantId) {
      params = params.append('consultantId', filters.consultantId.toString());
    }
    if (filters.visitPlanId) {
      params = params.append('visitPlanId', filters.visitPlanId.toString());
    }
    if (filters.status !== undefined && filters.status !== null) {
      params = params.append('status', filters.status.toString());
    }
    if (filters.type !== undefined && filters.type !== null) {
      params = params.append('type', filters.type.toString());
    }
    params = params.append('pageSize', filters.pageSize);
    params = params.append('pageNumber', filters.page);

    return this.http.get<Pagination<VisitCycleListItemDto>>(`${this.baseApiUrl}`, {
      params,
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  override getById(id: number): Observable<VisitCycleDetailsDto> {
    return this.http.get<VisitCycleDetailsDto>(`${this.baseApiUrl}/${id}`, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  getFilterOptions(): Observable<VisitCycleFilterOptionsDto> {
    return this.http.get<VisitCycleFilterOptionsDto>(`${this.baseApiUrl}/filter-options`, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  // ==================== Card Listing ====================

  getCards(filters: VisitCycleCardFilters): Observable<VisitCycleCardsResult> {
    let params = new HttpParams();

    if (filters.page) {
      params = params.append('page', filters.page.toString());
    }
    if (filters.pageSize) {
      params = params.append('pageSize', filters.pageSize.toString());
    }
    if (filters.search) {
      params = params.append('search', filters.search);
    }
    if (filters.consultantId) {
      params = params.append('consultantId', filters.consultantId.toString());
    }
    if (filters.visitPlanId) {
      params = params.append('visitPlanId', filters.visitPlanId.toString());
    }
    if (filters.status !== undefined && filters.status !== null) {
      params = params.append('status', filters.status.toString());
    }
    if (filters.type !== undefined && filters.type !== null) {
      params = params.append('type', filters.type.toString());
    }
    if (filters.periodStart) {
      params = params.append('periodStart', filters.periodStart);
    }
    if (filters.periodEnd) {
      params = params.append('periodEnd', filters.periodEnd);
    }
    if (filters.activityTag !== undefined && filters.activityTag !== null) {
      params = params.append('activityTag', filters.activityTag.toString());
    }

    return this.http.get<VisitCycleCardsResult>(`${this.baseApiUrl}/cards`, {
      params,
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  // ==================== CRUD ====================

  // @ts-ignore - return type differs from base class
  override create(request: CreateVisitCycleRequest): Observable<VisitCycleDetailsDto> {
    return this.http.post<VisitCycleDetailsDto>(`${this.baseApiUrl}`, request, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  updateVisitCycle(id: number, request: UpdateVisitCycleRequest, executionId?: number): Observable<VisitCycleDetailsDto> {
    const url = executionId
      ? `${this.baseApiUrl}/${id}?executionId=${executionId}`
      : `${this.baseApiUrl}/${id}`;
    return this.http.put<VisitCycleDetailsDto>(url, request, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  deleteVisitCycle(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseApiUrl}/${id}`, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  // ==================== Edição de VisitPlans (apenas ciclos ativos) ====================
  //
  // Uma chamada por escopo (template OU uma única execução Planned) recebe o diff completo
  // — add, remove, reorder e troca de consultor combinados — e o backend aplica tudo numa
  // única transação de banco. Qualquer falha faz rollback de todas as partes da mudança.

  editCycleTemplate(
    cycleId: number, diff: VisitPlanScopeDiff
  ): Observable<VisitCycleDetailsDto> {
    return this.http.put<VisitCycleDetailsDto>(
      `${this.baseApiUrl}/${cycleId}/template-edit`,
      diff,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  editCycleExecutionScope(
    cycleId: number, executionId: number, diff: VisitPlanScopeDiff
  ): Observable<VisitCycleDetailsDto> {
    return this.http.put<VisitCycleDetailsDto>(
      `${this.baseApiUrl}/${cycleId}/executions/${executionId}/scope-edit`,
      diff,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  // ==================== Fluxo de Criação (Wizard) ====================

  saveDraft(id: number, body?: { routeReorders: ReorderRouteEntryRequest[] }): Observable<VisitCycleDetailsDto> {
    return this.http.post<VisitCycleDetailsDto>(`${this.baseApiUrl}/${id}/save-draft`, body ?? {}, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  advanceStep(id: number, step: number): Observable<VisitCycleDetailsDto> {
    return this.http.post<VisitCycleDetailsDto>(`${this.baseApiUrl}/${id}/advance-step`, { step }, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  activate(id: number, request?: { routeReorders?: ReorderRouteEntryRequest[] }): Observable<VisitCycleDetailsDto> {
    return this.http.post<VisitCycleDetailsDto>(`${this.baseApiUrl}/${id}/activate`, request ?? {}, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  getRoute(cycleId: number, dayId: number): Observable<VisitCycleRouteDto> {
    return this.http.get<VisitCycleRouteDto>(
      `${this.baseApiUrl}/${cycleId}/days/${dayId}/route-details`,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  previewRouteStops(cycleId: number, dayId: number, request: { stops: RouteStopInput[]; workshop?: WorkshopRouteConstraint }): Observable<RouteOperationResult> {
    return this.http.post<RouteOperationResult>(
      `${this.baseApiUrl}/${cycleId}/days/${dayId}/preview`,
      request,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  previewExecutionRouteStops(
    cycleId: number,
    executionId: number,
    executionDayId: number,
    request: { stops: RouteStopInput[]; workshop?: WorkshopRouteConstraint; executionEditVersion?: string }
  ): Observable<RouteOperationResult> {
    return this.http.post<RouteOperationResult>(
      `${this.baseApiUrl}/${cycleId}/executions/${executionId}/days/${executionDayId}/preview`,
      request,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  // ==================== Gerenciamento de Dias ====================

  addDay(id: number, request: AddDayRequest, executionId?: number): Observable<VisitCycleDetailsDto> {
    const url = executionId
      ? `${this.baseApiUrl}/${id}/days?executionId=${executionId}`
      : `${this.baseApiUrl}/${id}/days`;
    return this.http.post<VisitCycleDetailsDto>(url, request, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  removeDay(id: number, dayId: number, executionId?: number): Observable<VisitCycleDetailsDto> {
    const url = executionId
      ? `${this.baseApiUrl}/${id}/days/${dayId}?executionId=${executionId}`
      : `${this.baseApiUrl}/${id}/days/${dayId}`;
    return this.http.delete<VisitCycleDetailsDto>(url, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  removeWorkshopOnly(cycleId: number, dayId: number, executionId?: number): Observable<VisitCycleDetailsDto> {
    const url = executionId
      ? `${this.baseApiUrl}/${cycleId}/days/${dayId}/workshop?executionId=${executionId}`
      : `${this.baseApiUrl}/${cycleId}/days/${dayId}/workshop`;
    return this.http.delete<VisitCycleDetailsDto>(url, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  restorePreWorkshopVersion(cycleId: number, dayId: number, executionId?: number): Observable<VisitCycleDetailsDto> {
    const url = executionId
      ? `${this.baseApiUrl}/${cycleId}/days/${dayId}/activity?executionId=${executionId}`
      : `${this.baseApiUrl}/${cycleId}/days/${dayId}/activity`;
    return this.http.delete<VisitCycleDetailsDto>(url, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  rotateDayDates(id: number, assignments: { dayId: number; newDate: string }[]): Observable<VisitCycleDetailsDto> {
    return this.http.put<VisitCycleDetailsDto>(
      `${this.baseApiUrl}/${id}/days/rotate-dates`,
      { assignments },
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  /**
   * Rotação de dias do template em ciclo Active/Paused.
   * newOrderByCycleDayId deve conter todos os VisitCycleDay.Id do ciclo na ordem desejada.
   * DayNumber é atualizado; VisitCycleDay.Id preservado; rotas/stops seguem o dia.
   * Após rotação, template é atualizado pra refletir a próxima execução materializável.
   */
  rotateTemplateDays(id: number, newOrderByCycleDayId: number[]): Observable<VisitCycleDetailsDto> {
    return this.http.put<VisitCycleDetailsDto>(
      `${this.baseApiUrl}/${id}/template/rotate-days`,
      { newOrderByCycleDayId },
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  updateDayAssignment(id: number, dayId: number, assignedDate?: string | null): Observable<VisitCycleDetailsDto> {
    return this.http.patch<VisitCycleDetailsDto>(`${this.baseApiUrl}/${id}/days/${dayId}`, { assignedDate: assignedDate ?? null }, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  assignDaysAutomatically(id: number, executionId?: number): Observable<VisitCycleDetailsDto> {
    const url = executionId
      ? `${this.baseApiUrl}/${id}/days/assign?executionId=${executionId}`
      : `${this.baseApiUrl}/${id}/days/assign`;
    return this.http.post<VisitCycleDetailsDto>(url, {}, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  pause(id: number): Observable<VisitCycleDetailsDto> {
    return this.http.post<VisitCycleDetailsDto>(`${this.baseApiUrl}/${id}/pause`, {}, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  resume(id: number): Observable<VisitCycleDetailsDto> {
    return this.http.post<VisitCycleDetailsDto>(`${this.baseApiUrl}/${id}/resume`, {}, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  cancel(id: number): Observable<VisitCycleDetailsDto> {
    return this.http.post<VisitCycleDetailsDto>(`${this.baseApiUrl}/${id}/cancel`, {}, {
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  // ==================== Calendário ====================

  /**
   * Calendar scope-aware.
   * - executionId omitido → scope Template: retorna cycle.Days; DTO.id = VisitCycleDay.Id.
   * - executionId com valor → scope Execução N: retorna só essa exec; DTO.id = ExecutionDay.Id.
   */
  getCalendar(id: number, year: number, month: number, executionId?: number): Observable<VisitCycleCalendarDto> {
    let params = new HttpParams()
      .set('year', year.toString())
      .set('month', month.toString());

    if (executionId != null) {
      params = params.set('executionId', executionId.toString());
    }

    return this.http.get<VisitCycleCalendarDto>(`${this.baseApiUrl}/${id}/calendar`, {
      params,
      context: new HttpContext().set(SKIP_LOADING, true)
    });
  }

  // ==================== Gerenciamento de Paradas da Rota ====================

  addRouteStop(cycleId: number, dayId: number, request: AddRouteStopRequest): Observable<VisitCycleRouteStopDto> {
    return this.http.post<VisitCycleRouteStopDto>(
      `${this.baseApiUrl}/${cycleId}/days/${dayId}/stops`,
      request,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  removeRouteStop(cycleId: number, dayId: number, stopId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseApiUrl}/${cycleId}/days/${dayId}/stops/${stopId}`,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  updateRouteStop(
    cycleId: number,
    dayId: number,
    stopId: number,
    request: UpdateRouteStopRequest
  ): Observable<VisitCycleRouteStopDto> {
    return this.http.patch<VisitCycleRouteStopDto>(
      `${this.baseApiUrl}/${cycleId}/days/${dayId}/stops/${stopId}`,
      request,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  applyWorkshopRoute(cycleId: number, dayId: number, request: ApplyWorkshopRequest, etag: string): Observable<VisitCycleDetailsDto> {
    return this.http.put<VisitCycleDetailsDto>(
      `${this.baseApiUrl}/${cycleId}/days/${dayId}/route`,
      request,
      {
        headers: new HttpHeaders().set('If-Match', etag),
        context: new HttpContext().set(SKIP_LOADING, true)
      }
    );
  }

  // ==================== Edição de Execução ====================

  getExecutionRoute(cycleId: number, executionId: number, dayId: number): Observable<VisitCycleRouteDto> {
    return this.http.get<VisitCycleRouteDto>(
      `${this.baseApiUrl}/${cycleId}/executions/${executionId}/days/${dayId}/route-details`,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  getExecutionDays(cycleId: number, executionId: number): Observable<VisitCycleDayDto[]> {
    return this.http.get<VisitCycleDayDto[]>(
      `${this.baseApiUrl}/${cycleId}/executions/${executionId}/days`,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  saveExecutionDraft(cycleId: number, executionId: number, body?: { routeReorders: ReorderRouteEntryRequest[] }): Observable<VisitCycleExecutionSummaryDto> {
    return this.http.post<VisitCycleExecutionSummaryDto>(
      `${this.baseApiUrl}/${cycleId}/executions/${executionId}/save-draft`,
      body ?? {},
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  // Preview cascade effect of calendar changes without persisting
  previewCycleCalendarChanges(
    cycleId: number,
    executionId: number,
    body: { dayAssignments?: DayAssignmentChangeDto[]; routeReorders?: ReorderRouteEntryRequest[] }
  ): Observable<CalendarChangesPreviewDto> {
    return this.http.post<CalendarChangesPreviewDto>(
      `${this.baseApiUrl}/${cycleId}/executions/${executionId}/preview-calendar-changes`,
      body,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  // Atomic batch apply of calendar changes (dayAssignments + routeReorders)
  applyCycleCalendarChanges(
    cycleId: number,
    executionId: number,
    body: { dayAssignments?: DayAssignmentChangeDto[]; routeReorders?: ReorderRouteEntryRequest[] }
  ): Observable<CalendarChangesResultDto> {
    return this.http.post<CalendarChangesResultDto>(
      `${this.baseApiUrl}/${cycleId}/executions/${executionId}/apply-calendar-changes`,
      body,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  addExecutionRouteStop(cycleId: number, executionId: number, dayId: number, request: AddRouteStopRequest): Observable<void> {
    return this.http.post<void>(
      `${this.baseApiUrl}/${cycleId}/executions/${executionId}/days/${dayId}/stops`,
      request,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }

  removeExecutionRouteStop(cycleId: number, executionId: number, dayId: number, stopId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseApiUrl}/${cycleId}/executions/${executionId}/days/${dayId}/stops/${stopId}`,
      { context: new HttpContext().set(SKIP_LOADING, true) }
    );
  }
}
